import { createContext, Dispatch, FC, SetStateAction, useContext, useState } from 'react';
import { Box, Button, Card, CardContent, FormHelperText, Typography, TextField, Grid, Container, FormControl, OutlinedInput } from '@mui/material';
import * as Yup from 'yup'
import { UIFormState, usePlainStateFormUtils } from '@/components/input/usePlainStateFormUtils';
import Create from '@/controller/Create';
import WithSave from '@/controller/WithSave';
import { rPassword } from '../LoginContext';
import CreateOrEdit from '@/controller/CreateOrEdit';
import { useDispatch } from 'react-redux';
import { push } from 'connected-react-router';
import { useDefaultSavedHandlers } from '@/controller/defaultSavedHandlers';


export enum PassRstStep {
  Init,
  VCodeSent,
  CollectNewPass,
}
const emptySchema =
  Yup.object().shape({})

interface UIStates {
  prevData: {
    email: string;
    codeJwt: string;
    cid: string;
  };
  stage: {
    newPass: string;
    confirmPass: string;
    smsVcode: string;
    smsVcodeId: number;
  };
  errors: any;
  touched: any;
  vcodeSent: boolean;
  status: PassRstStep;
}


export const RstPassValidateVcode: FC<{states: UIStates, setStates: Dispatch<SetStateAction<UIStates>>}> = ({
  states,
  setStates,
}) => {
  const { fieldProps } = usePlainStateFormUtils(states, setStates, emptySchema)
  const [countdown, setCountdown] = useState({
    lastCodeSendTime: null as Date|null,
    timer: null as number|null,
    remain: 0,
  })
  const [wait, setWait] = useState(false)
  
  const requestVcode = (save: (record: any, callbacks?: any)=>void) => {
    setStates(v => ({...v, stage: {...v.stage, smsVcode: ''}, errors: {}}))
    save({ cid: states.prevData.cid, codeJwt: states.prevData.codeJwt }, {
      onSuccess: ({data}: {data: any}) => {
        setStates(v => ({...v, 
          stage: {...v.stage,
            smsVcodeId: data.data,
          },
          vcodeSent: true,
          status: PassRstStep.VCodeSent,
        }))
        startCountdown()
      },
      onFailure: (err: any) => {
        setStates(v => {
          const v1 = {...v}
          const errMsg: string = err?.message
          v1.errors = {...v.errors, submit: errMsg }
          return v1  
        })
      },
    })
  }
  const startCountdown = () => {
    const timer = window.setInterval(() => {
      setCountdown(c => {
        const c1 = {...c, 
          remain: 60 - Math.round(((new Date().getTime()) - (c.lastCodeSendTime!).getTime()) / 1000)
        }
        if (c1.remain <= 0) {
          window.clearInterval(c.timer!)
          c1.timer = null
          c1.lastCodeSendTime = null
          c1.remain = 0
        }
        return c1
      })
    }, 500)
    setCountdown({ lastCodeSendTime: new Date(), timer, remain: 60 })
  }
  const requestVerifyCode = (save: (record: any, callbacks?: any)=>void) => {
    const payload = {
      cid: states.prevData.cid,
      code: states.prevData.codeJwt,
      smsVcode: states.stage.smsVcode,
      smsVcodeId: states.stage.smsVcodeId,
    }
    setStates(v => ({...v, errors: {}}))
    save(payload, {
      onSuccess: () => {
        setStates(v => ({...v,
          status: PassRstStep.CollectNewPass,
        }))
      },
      onFailure: (err: any) => {
        setStates(v => ({...v, errors: {...v.errors, submit: err?.message }}))
      },
    })
  }

  return (
    <Card role="application" aria-labelledby="pass-recover-title" aria-describedby="pass-recover-description">
      <CardContent sx={{ display: 'flex', flexDirection: 'column', p: 4 }} >
        <Box sx={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between', mb: 3 }} >
          <div>
            <Typography color="textPrimary" gutterBottom variant="h4" id="pass-recover-title" >
              Password Recovery
            </Typography>
            <Typography color="textSecondary" variant="body2" id="pass-recover-description" >
              Forgot your password? We can help! <br/>
              Please send a one-time passcode to your phone and verify the passcode below.
            </Typography>
          </div>
        </Box>
        <Box sx={{ flexGrow: 1, mt: 3 }} >
          <form>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField margin="normal" name="email" type="email" fullWidth
                  label="Email Address"
                  value={states.prevData.email}
                />
              </Grid>
              {states.vcodeSent &&
                <Grid container item xs={12} spacing={1}>
                  <Grid item xs={8}>
                    <TextField label="Verification Code" name="vcode" autoFocus fullWidth {...fieldProps('smsVcode')} />
                  </Grid>
                  <Grid item xs={4} sx={{ alignSelf: 'center'}}>
                    <Create resource="resetpassword-validate" basePath="">
                      <WithSave
                        render={({save, saving}) =>
                          <Button variant="text" sx={{ width: '100%' }}
                            disabled={saving || wait || countdown.remain > 0}
                            onClick={() => requestVcode(save)}
                          >
                            <Typography color={saving || countdown.remain > 0 ? 'text.disabled' : 'secondary'} variant="button" sx={{ textTransform: 'unset'}}>
                              Send Code Again
                            </Typography>
                          </Button>
                        }
                      />
                    </Create>
                  </Grid>
                </Grid>}

              {states.errors.submit && (
                <Box sx={{ mt: 3, ml: 4 }}>
                  <FormHelperText error>
                    {states.errors.submit}
                  </FormHelperText>
                </Box>
              )}

              <Grid item xs={12} >
                <Box sx={{ mt: 3 }}>
                  {states.status === PassRstStep.Init &&
                    <Create resource="resetpassword-validate" basePath="">
                      <WithSave
                        render={({save, saving}) =>
                          <Button variant="contained" color="primary" fullWidth size="large" type="submit"
                            disabled={saving || wait}
                            onClick={() => requestVcode(save)}
                          >
                            Send Code to My Phone
                          </Button>
                        }
                      />
                    </Create>}
                  {states.status === PassRstStep.VCodeSent &&
                    <CreateOrEdit id="sms" resource="resetpassword-validate" basePath="" record={{id: "sms"} as any}>
                      <WithSave
                        render={({save, saving}) =>
                          <Button variant="contained" fullWidth size="large" type="submit"
                            disabled={!states.stage.smsVcode || saving}
                            onClick={(e) => { requestVerifyCode(save); e.preventDefault() }}
                          >
                            Verify Code
                          </Button>
                        }
                      />
                    </CreateOrEdit>}
                </Box>
              </Grid>
            </Grid>
          </form>
        </Box>
      </CardContent>
    </Card>
  )
}

const newPassSchema =
  Yup.object().shape({
    newPass: Yup
      .string()
      .min(12)
      .max(255)
      .matches(
        rPassword, 
        'Password must contain uppercase and lowercase characters, numbers and special characters')
      .required('Password is required'),
    confirmPass: Yup
      .string()
      .min(12)
      .max(255)
      .matches(
        rPassword, 
        'Password must contain uppercase and lowercase characters, numbers and special characters')
      .oneOf([Yup.ref('newPass'), null], 'Passwords must match')
      .required('Confirm Password is required'),
  })
  
interface RstPassCollectContextProps {
  resource: string;
  extraOptions?: any;
  getPayload: () => any;
}
export const RstPassCollectContext = createContext<RstPassCollectContextProps>({
  resource: '',
  getPayload: ()=>{},
})

export const RstPassCollectDefaultProvider: FC<{states: UIStates}> = ({states, children}) => {
  const value = {
    resource: 'resetpassword-confirm',
    getPayload: () => ({
      cid: states.prevData.cid,
      code: states.prevData.codeJwt,
      smsVcode: states.stage.smsVcode,
      smsVcodeId: states.stage.smsVcodeId,
      password: states.stage.newPass,
      confirmPassword: states.stage.confirmPass,
    })
  }
  return (
    <RstPassCollectContext.Provider value={value}>
      {children}
    </RstPassCollectContext.Provider>
  )
}

interface S1 {
  newPass: string;
  confirmPass: string;
}
interface S2 {
  email: string;
}
type S3 = UIFormState<S1, S2>

export const RstPassCollect: FC<{states: S3, setStates: Dispatch<SetStateAction<S3>>}> = ({
  states,
  setStates,
}) => {
  const { validate, fieldProps } = usePlainStateFormUtils(states, setStates, newPassSchema)
  const dispatch = useDispatch()
  const handlers = useDefaultSavedHandlers("Password Reset")
  const {resource, getPayload, extraOptions} = useContext(RstPassCollectContext)

  const submitPassReset = (save: (record: any, callbacks?: any)=>void) => {
    const errors = validate(states.stage)
    if (errors) {
      setStates(v => ({...v, errors, touched: {...v.touched, newPass: true, confirmPass: true}}))
      return
    }
    setStates(v => ({...v, errors: {}}))
    const payload = getPayload()
    save(payload, {
      onSuccess: () => {
        dispatch(push('/login'))
      },
      onFailure: (err: any) => {
        setStates(v => ({...v, errors: {...v.errors, submit: err?.message }}))
      },
    })
  }
  
  return (
    <Box>
      <Typography color="textPrimary" variant="h5"
        sx={{ 
          fontWeight: 'fontWeightBold',
          textAlign: 'center',
          pb: 3,
        }}
      >
        Please choose a new password for your account.
      </Typography>

      <Card
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            lg: 'repeat(2, 1fr)',
            xs: 'repeat(1, 1fr)'
          },
          // minHeight: '100%'
        }}
      >
        <Box sx={{ pt: 8 }} >
          <Container maxWidth="md" sx={{ pl: { lg: 15 } }} >
            <Typography sx={{ color: 'primary.main', pb: 3 }} variant="h6" >
              Password Guidelines
            </Typography>
            <Typography color="textPrimary" sx={{ py: 0.5 }} variant="body1" >
              1. Password must be 12 or more characters long.
            </Typography>
            <Typography color="textPrimary" sx={{ py: 0.5 }} variant="body1" >
              2. Password must contain uppercase and lowercase characters, numbers and special characters.
            </Typography>
          </Container>
        </Box>
        <Box sx={{ backgroundColor: 'background.paper', pt: 8, pb: 3, }}>
          <Container maxWidth="md" sx={{ pr: { lg: 8 } }} >
            <Box sx={{}}>
              <Typography color="textPrimary" variant="h6" sx={{ pb: 1 }} >
                Email Address
              </Typography>
              <FormControl variant="outlined" fullWidth sx={{ pb: 3 }} >
                <OutlinedInput disabled inputProps={{ 'aria-label': 'email address' }}
                  value={states.prevData.email || ''}
                />
              </FormControl>

              <form>
                <Typography color="textPrimary" variant="h6" sx={{ pb: 0 }} >
                  Choose a Password
                </Typography>
                <TextField type="password" label="Password" autoFocus name="password" fullWidth sx={{ mt: 2, mb: 1}} 
                  {...fieldProps('newPass')}
                />
                <TextField type="password" label="Confirm Password" name="confirmPassword" fullWidth sx={{ mt: 2, mb: 1}} 
                  {...fieldProps('confirmPass')}
                />
                {states.errors.submit && (
                  <Box sx={{ mt: 3 }}>
                    <FormHelperText error>
                      {states.errors.submit}
                    </FormHelperText>
                  </Box>
                )}
                <Box sx={{ mt: 4 }} >
                  <CreateOrEdit resource={resource} basePath="" {...extraOptions} {...handlers}>
                    <WithSave
                      render={({save, saving}) =>
                        <Button fullWidth color="primary" size="large" type="submit" variant="contained"
                          disabled={saving || !states.stage.newPass || !states.stage.confirmPass}
                          onClick={() => submitPassReset(save)}
                        >
                          Submit
                        </Button>
                      }
                    />
                  </CreateOrEdit>
                </Box>
              </form>
            </Box>
          </Container>
        </Box>
      </Card>
    </Box>
  )
}

